import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { BsCheckAll, BsCheckLg } from "react-icons/bs";
import { useChatBubbleSettings } from "../../../Context/ChatBubbleBgContext";
import { BeatLoader, FadeLoader } from "react-spinners";
import VoiceMessageBubble from "../../Group/ChatsSection/VoiceMessageBubble";
import {
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileAlt,
  FaFile,
  FaFileVideo,
  FaFont,
  FaRegSmile,
  FaUser,
} from "react-icons/fa";
import { IoChevronDownOutline } from "react-icons/io5";

import {
  decryptMessage,
  decryptText,
  encrypt,
} from "../../../Components/EncryptDecryptMessage/EncryptDecryptMessage";
import Modal from "../../../Components/Modals/Modall";
import { Modal as ResponsiveModal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import {
  deleteApiCaller,
  putApiCaller,
  updateApiCaller,
} from "../../../Libs/apiCaller";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import EmojiPicker from "emoji-picker-react";
import MapMessage from "../../../Components/MapMessage/MapMessage";
import ReactionPicker from "../../../Components/MessageReactions/ReactionPicker";
import ReactionPills from "../../../Components/MessageReactions/ReactionPills";
import { HiOutlineEmojiHappy } from "react-icons/hi";
import { useTheme } from "../../../Context/ThemeContext";
const getFileExtension = (url) => {
  return url?.split(".").pop().toLowerCase();
};

const FileIcon = ({ url }) => {
  const ext = getFileExtension(url);

  const iconMap = {
    pdf: <FaFilePdf className="size-15 text-red-500" />,
    doc: <FaFileWord className="size-15 text-blue-600" />,
    docx: <FaFileWord className="size-15 text-blue-600" />,
    xls: <FaFileExcel className="size-15 text-green-600" />,
    xlsx: <FaFileExcel className="size-15 text-green-600" />,
    ppt: <FaFilePowerpoint className="size-15 text-orange-500" />,
    pptx: <FaFilePowerpoint className="size-15 text-orange-500" />,
    jpg: <FaFileImage className="size-15 text-green-500" />,
    png: <FaFileImage className="size-15 text-green-500" />,
    txt: <FaFileAlt className="size-15 text-gray-500" />,
    ttf: <FaFont className="size-15 text-purple-500" />,
    otf: <FaFont className="size-15 text-purple-500" />,
    ttc: <FaFont className="size-15 text-purple-500" />,
  };

  return iconMap[ext] || <FaFile className="text-gray-400" />;
};

const linkify = (text) => {
  // Escape HTML to prevent script injection
  const escapeHtml = (str) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // Regex to detect URLs with or without protocol
  const urlPattern =
    /(?:https?:\/\/)?(?:www\.)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/g;

  // First escape the text
  const safeText = escapeHtml(text);

  // Then safely replace URLs
  return safeText.replace(urlPattern, (url) => {
    // Add https:// if no protocol is present
    const fullUrl = url.startsWith("http") ? url : `https://${url}`;
    return `<a href="${fullUrl}" class="underline hover:opacity-80" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
};

export default function ChatsSection({
  allMessages,
  setAllMessages,
  getSecretKey,
  settings,
  chatConversationId,
  isMessageLoading,
  isDecryptionLoading,
  setIsDecryptionLoading,
  socket,
  activeChat,
  setChatUsers,
  isTyping = false,
  activeMessageId, // Accept activeMessageId prop
  setActiveMessageId,
  localSearchQuery,
  triggerNext,
  triggerPrev,
  setSearchStats,
}) {
  const { userInfo } = useSelector((state) => state.authUser);
  const { bgColorBubble } = useChatBubbleSettings();
  const { theme } = useTheme();
  const uid = localStorage.getItem("userId");
  const [messages, setMessages] = useState([]);
  const lastDecryptedRef = useRef(null); // Track last decrypted message IDs to prevent unnecessary re-decryption
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [deleteMessageId, setDeleteMessageId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const dropdownRefs = useRef([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [isDeleteFor, setIsDeleteFor] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);

  // Local Search State in ChatsSection
  const [matchedIds, setMatchedIds] = useState([]);
  const [currentMatchIdx, setCurrentMatchIdx] = useState(-1);

  // Search Logic (Decrypted Messages)
  useEffect(() => {
    if (!localSearchQuery || !localSearchQuery.trim()) {
      setMatchedIds([]);
      setCurrentMatchIdx(-1);
      if (setSearchStats) setSearchStats({ current: 0, total: 0 });
      return;
    }

    const query = localSearchQuery.toLowerCase();
    // Search in DECRYPTED messages state
    const matches = messages
      .filter(msg => {
        // Check cipherText (which is now decrypted text)
        const text = msg.cipherText || "";
        return text.toLowerCase().includes(query);
      })
      .map(msg => msg.messageId || msg._id);

    setMatchedIds(matches);
    const total = matches.length;

    if (total > 0) {
      setCurrentMatchIdx(0);
      if (setSearchStats) setSearchStats({ current: 0, total });
      if (setActiveMessageId) setActiveMessageId(matches[0]);
    } else {
      setCurrentMatchIdx(-1);
      if (setSearchStats) setSearchStats({ current: 0, total: 0 });
    }
  }, [localSearchQuery, messages.length]); // Re-run when query changes or new messages arrive

  // Navigation Logic
  useEffect(() => {
    if (matchedIds.length === 0) return;
    const nextIdx = (currentMatchIdx + 1) % matchedIds.length;
    setCurrentMatchIdx(nextIdx);
    if (setSearchStats) setSearchStats({ current: nextIdx, total: matchedIds.length });
    if (setActiveMessageId) setActiveMessageId(matchedIds[nextIdx]);
  }, [triggerNext]);

  useEffect(() => {
    if (matchedIds.length === 0) return;
    const prevIdx = (currentMatchIdx - 1 + matchedIds.length) % matchedIds.length;
    setCurrentMatchIdx(prevIdx);
    if (setSearchStats) setSearchStats({ current: prevIdx, total: matchedIds.length });
    if (setActiveMessageId) setActiveMessageId(matchedIds[prevIdx]);
  }, [triggerPrev]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefs.current[openDropdownIndex] &&
        !dropdownRefs.current[openDropdownIndex].contains(event.target)
      ) {
        setOpenDropdownIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownIndex]);

  // Listen for reaction updates via socket
  useEffect(() => {
    if (!socket) return;

    const handleReactionUpdate = (data) => {
      const { messageId, reactions } = data;

      // Update the message with new reactions
      setAllMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.messageId === messageId
            ? { ...msg, reactions: reactions || [] }
            : msg
        )
      );
    };

    socket.on('reaction_updated', handleReactionUpdate);

    return () => {
      socket.off('reaction_updated', handleReactionUpdate);
    };
  }, [socket, setAllMessages]);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const thisYear = now.getFullYear();
    const messageYear = date.getFullYear();

    // Time format
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    const time = date.toLocaleString("en-US", timeOptions);

    // If message is from Today, show only time
    if (messageDate.getTime() === today.getTime()) {
      return time;
    }

    // If message is from This Year, show Month/Day + Time
    if (messageYear === thisYear) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      return `${month} ${day}, ${time}`;
    }

    // If message is from Previous Year, show full date with year
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Helper function to format date for date separator (Month Day, Year)
  const formatDateSeparator = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const thisYear = now.getFullYear();
    const messageYear = date.getFullYear();

    // If message is from Today
    if (messageDate.getTime() === today.getTime()) {
      return "Today";
    }

    // If message is from Yesterday
    if (messageDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    }

    // If message is from This Year, show Month Day
    if (messageYear === thisYear) {
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      return `${month} ${day}`;
    }

    // If message is from Previous Year, show Month Day, Year
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}, ${messageYear}`;
  };

  // Helper function to check if two messages are from different dates
  const isDifferentDate = (msg1, msg2) => {
    if (!msg1 || !msg2 || !msg1.sentTime || !msg2.sentTime) return true;
    const date1 = new Date(msg1.sentTime);
    const date2 = new Date(msg2.sentTime);
    return (
      date1.getFullYear() !== date2.getFullYear() ||
      date1.getMonth() !== date2.getMonth() ||
      date1.getDate() !== date2.getDate()
    );
  };

  // Helper function to determine message stack position
  const getMessageStackPosition = (currentMsg, prevMsg, nextMsg, uid) => {
    // Helper to extract senderId handling both object and string formats
    const getSenderId = (msg) => {
      if (!msg?.senderId) return null;
      return msg.senderId._id || (typeof msg.senderId === 'string' ? msg.senderId : null);
    };
    const currentSender = getSenderId(currentMsg);
    const prevSender = getSenderId(prevMsg);
    const nextSender = getSenderId(nextMsg);

    // Calculate time gap in milliseconds (5 minutes = 300000ms)
    // Messages are in chronological order (oldest first)
    const timeGap = (earlierMsg, laterMsg) => {
      if (!earlierMsg || !laterMsg || !earlierMsg.sentTime || !laterMsg.sentTime) return Infinity;
      const earlierTime = new Date(earlierMsg.sentTime).getTime();
      const laterTime = new Date(laterMsg.sentTime).getTime();
      return laterTime - earlierTime; // Should be positive if messages are in order
    };

    // Check if messages are in sequence (same user AND < 5min gap)
    const isSequence = (msg1, msg2) => {
      if (!msg1 || !msg2) return false;
      const getSenderId = (msg) => {
        if (!msg?.senderId) return null;
        return msg.senderId._id || (typeof msg.senderId === 'string' ? msg.senderId : null);
      };
      const sender1 = getSenderId(msg1);
      const sender2 = getSenderId(msg2);
      if (!sender1 || !sender2 || String(sender1) !== String(sender2)) return false;
      const gap = timeGap(msg1, msg2);
      return gap <= 300000; // 5 minutes in milliseconds
    };

    const isPrevInSequence = prevMsg ? isSequence(prevMsg, currentMsg) : false;
    const isNextInSequence = nextMsg ? isSequence(currentMsg, nextMsg) : false;

    // Determine position
    if (!isPrevInSequence && !isNextInSequence) return 'single';
    if (!isPrevInSequence && isNextInSequence) return 'top';
    if (isPrevInSequence && !isNextInSequence) return 'bottom';
    return 'middle'; // isPrevInSequence && isNextInSequence
  };

  // Helper function to get border radius classes based on position
  const getBorderRadiusClasses = (position, isSender) => {
    if (isSender) {
      // SENT Messages (Deep Blue Gradient)
      switch (position) {
        case 'single':
          return 'rounded-[20px] rounded-br-sm mb-4';
        case 'top':
          return 'rounded-[20px] rounded-br-md mb-[2px]';
        case 'middle':
          return 'rounded-l-[20px] rounded-r-md mb-[2px]';
        case 'bottom':
          return 'rounded-l-[20px] rounded-br-[20px] rounded-tr-md mb-4';
        default:
          return 'rounded-[20px] rounded-br-sm mb-4';
      }
    } else {
      // RECEIVED Messages (White Glass)
      switch (position) {
        case 'single':
          return 'rounded-[20px] rounded-bl-sm mb-4';
        case 'top':
          return 'rounded-[20px] rounded-bl-md mb-[2px]';
        case 'middle':
          return 'rounded-r-[20px] rounded-l-md mb-[2px]';
        case 'bottom':
          return 'rounded-r-[20px] rounded-bl-[20px] rounded-tl-md mb-4';
        default:
          return 'rounded-[20px] rounded-bl-sm mb-4';
      }
    }
  };

  const downloadFile = (url) => {
    const ext = getFileExtension(url);
    if (
      ext === "pdf" ||
      ext === "jpg" ||
      ext === "png" ||
      ext === "jpeg" ||
      ext === "gif" ||
      ext === "mp4" ||
      ext === "mov" ||
      ext === "ttf" ||
      ext === "otf" ||
      ext === "ttc"
    ) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  function getContrastingTextColor(hex) {
    // Remove "#" if present
    hex = hex.replace("#", "");

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return black for light backgrounds, white for dark backgrounds
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  const fgColor = useMemo(() => {
    const fgColor = settings?.chatColor
      ? `${getContrastingTextColor(settings?.chatColor)}`
      : "#fff";

    return `${fgColor}`;
  }, [settings]);

  useEffect(() => {
    const decryptAllMessages = async () => {
      // Create a signature of current messages to detect if they actually changed
      // Use messageId instead of _id since _id can change from temp to real ID
      // Also include message count and first/last messageIds to catch additions/removals
      const messageCount = allMessages.length;
      const firstMessageId = allMessages[0]?.messageId || '';
      const lastMessageId = allMessages[allMessages.length - 1]?.messageId || '';
      const contentSignature = allMessages.slice(0, 20).map(m => ({
        messageId: m.messageId,
        cipherText: m.cipherText?.substring(0, 50) || '', // First 50 chars of cipherText
        iv: m.iv || '',
        senderId: m.senderId?._id || m.senderId || '',
        loading: m.loading || false // CRITICAL: Include loading state in signature
      })).map(m => `${m.messageId}:${m.cipherText}:${m.iv}:${m.senderId}:${m.loading}`).join('|');

      const currentSignature = `${messageCount}:${firstMessageId}:${lastMessageId}:${contentSignature}`;

      // FAST PATH: If only loading or mediaUrl changed (no content changes), update immediately
      // This prevents spinner from hanging when socket updates loading state
      if (lastDecryptedRef.current && messages.length > 0 && messages.length === allMessages.length) {
        // Check if only loading or mediaUrl changed by comparing first few messages
        const hasOnlyStateChanges = allMessages.slice(0, 5).every((newMsg, idx) => {
          const oldMsg = messages[idx];
          if (!oldMsg) return false;

          // Same message ID and content, but different loading or mediaUrl
          const sameContent = oldMsg.messageId === newMsg.messageId &&
            oldMsg.cipherText === newMsg.cipherText &&
            oldMsg.iv === newMsg.iv;
          const differentState = oldMsg.loading !== newMsg.loading ||
            oldMsg.mediaUrl !== newMsg.mediaUrl;

          return sameContent && (differentState || (!differentState && sameContent));
        });

        if (hasOnlyStateChanges) {
          console.log('[DECRYPT] Fast path: Only loading/mediaUrl changed, updating immediately');
          // CRITICAL: Create a NEW array reference so React detects the change
          // Shallow copy is enough since we're just updating references
          setMessages([...allMessages]);
          lastDecryptedRef.current = currentSignature;
          return;
        }
      }

      // Skip if messages haven't actually changed (prevents re-decryption on socket updates)
      // BUT allow update if loading state changed (to remove spinner)
      if (lastDecryptedRef.current === currentSignature && messages.length > 0) {
        return;
      }


      // If no secret key but we have messages, wait a bit for the key to be set
      // This prevents decrypting with wrong key from previous conversation
      if (!getSecretKey) {
        if (allMessages.length > 0) {
          // Wait briefly for secret key to be set (handles race condition)
          setTimeout(() => {
            if (!getSecretKey && allMessages.length > 0) {
              setIsDecryptionLoading(false);
              setMessages(allMessages);
            }
          }, 100);
        }
        return;
      }

      if (!allMessages.length) {
        setMessages([]);
        setIsDecryptionLoading(false);
        return;
      }

      // OPTIMIZATION: Decrypt incrementally - visible messages first (last 20), then rest
      // This makes the UI feel instant while decrypting in background
      const visibleCount = 20;
      const visibleMessages = allMessages.slice(0, visibleCount);
      const restMessages = allMessages.slice(visibleCount);

      // Helper to normalize senderId structure - ensure it's always { _id: "..." }
      const normalizeSenderId = (message) => {
        if (!message || !message.senderId) return message;
        if (typeof message.senderId === 'object' && message.senderId._id) {
          // Already in correct format
          return message;
        } else if (typeof message.senderId === 'string') {
          // Convert string to object format
          return { ...message, senderId: { _id: message.senderId } };
        } else if (message.senderId && message.senderId._id) {
          // Has _id property, ensure it's in object format
          return { ...message, senderId: { _id: message.senderId._id } };
        }
        return message;
      };

      // Decrypt visible messages first (user sees these immediately)
      const decryptedVisible = await Promise.all(
        visibleMessages.map(async (msg) => {
          // Normalize senderId BEFORE processing
          const normalizedMsg = normalizeSenderId(msg);

          // If message is already decrypted (has 'message' field), use it
          if (normalizedMsg?.message && !normalizedMsg?.cipherText) {
            return normalizedMsg;
          }

          // If no cipherText, return as-is (but normalized)
          if (!normalizedMsg?.cipherText) {
            return normalizedMsg;
          }

          // Only decrypt if we have a secret key
          if (!getSecretKey) {
            return normalizedMsg; // Return as-is if no key
          }

          try {
            const decryptedText = await decryptMessage(
              normalizedMsg?.cipherText,
              normalizedMsg.iv,
              getSecretKey
            );
            // CRITICAL: Preserve all message fields, especially senderId structure
            // Ensure senderId is always an object with _id property
            const preservedSenderId = normalizedMsg.senderId
              ? (typeof normalizedMsg.senderId === 'object' && normalizedMsg.senderId._id
                ? normalizedMsg.senderId
                : { _id: normalizedMsg.senderId._id || normalizedMsg.senderId })
              : null;

            return {
              ...normalizedMsg,
              cipherText: decryptedText,
              // Explicitly preserve senderId structure - always ensure it's { _id: "..." }
              senderId: preservedSenderId,
              // CRITICAL: Preserve loading and mediaUrl from original message
              // These should not be lost during decryption
              loading: normalizedMsg.loading || false,
              mediaUrl: normalizedMsg.mediaUrl || null,
            };
          } catch (err) {
            // Decryption failed - might be wrong key or corrupted data
            console.error('[DECRYPT] Decryption failed:', {
              error: err.message,
              errorName: err.name,
              messageId: msg.messageId || msg._id,
              cipherTextLength: msg?.cipherText?.length,
              cipherTextPreview: msg?.cipherText?.substring(0, 50),
              ivLength: msg?.iv?.length,
              ivPreview: msg?.iv?.substring(0, 32),
              hasSecretKey: !!getSecretKey,
              secretKeyLength: getSecretKey?.length
            });

            // Check if cipherText looks like encrypted hex data
            const isEncryptedHex = msg?.cipherText && /^[0-9a-fA-F\s]+$/.test(msg.cipherText.replace(/\s/g, ''));
            if (isEncryptedHex) {
              // This is encrypted data that failed to decrypt - show placeholder
              // Only log first few failures to reduce console noise
              return {
                ...msg,
                cipherText: "[Encrypted message - unable to decrypt]",
                decryptionFailed: true,
                // Explicitly preserve senderId
                senderId: msg.senderId || { _id: msg.senderId?._id || msg.senderId },
                // CRITICAL: Preserve loading and mediaUrl
                loading: msg.loading || false,
                mediaUrl: msg.mediaUrl || null,
              };
            }
            // Not encrypted hex, might be plain text or already decrypted
            // Still preserve senderId structure
            return {
              ...msg,
              senderId: msg.senderId || { _id: msg.senderId?._id || msg.senderId },
              // CRITICAL: Preserve loading and mediaUrl
              loading: msg.loading || false,
              mediaUrl: msg.mediaUrl || null,
            };
          }
        })
      );

      // Show visible messages immediately

      // Update signature after decryption (use the same format as the check)
      // Use the ORIGINAL allMessages, not decryptedVisible, to match the check format
      const visibleMessageCount = allMessages.length;
      const visibleFirstMessageId = allMessages[0]?.messageId || '';
      const visibleLastMessageId = allMessages[allMessages.length - 1]?.messageId || '';
      const visibleContentSignature = allMessages.slice(0, 20).map(m => ({
        messageId: m.messageId,
        cipherText: m.cipherText?.substring(0, 50) || '',
        iv: m.iv || '',
        senderId: m.senderId?._id || m.senderId || ''
      })).map(m => `${m.messageId}:${m.cipherText}:${m.iv}:${m.senderId}`).join('|');
      const visibleSignature = `${visibleMessageCount}:${visibleFirstMessageId}:${visibleLastMessageId}:${visibleContentSignature}`;
      lastDecryptedRef.current = visibleSignature; // Will be updated again after background decryption

      setIsDecryptionLoading(false);
      setMessages([...decryptedVisible, ...restMessages]);

      // Decrypt rest in background (non-blocking)
      if (restMessages.length > 0 && getSecretKey) {
        Promise.all(
          restMessages.map(async (msg) => {
            // Normalize senderId BEFORE processing
            const normalizedMsg = normalizeSenderId(msg);

            // If message is already decrypted, use it
            if (normalizedMsg?.message && !normalizedMsg?.cipherText) {
              return normalizedMsg;
            }

            // If no cipherText, return as-is (but normalized)
            if (!normalizedMsg?.cipherText) {
              return normalizedMsg;
            }

            try {
              const decryptedText = await decryptMessage(
                normalizedMsg?.cipherText,
                normalizedMsg.iv,
                getSecretKey
              );
              // CRITICAL: Preserve all message fields, especially senderId structure
              // Ensure senderId is always an object with _id property
              const preservedSenderId = normalizedMsg.senderId
                ? (typeof normalizedMsg.senderId === 'object' && normalizedMsg.senderId._id
                  ? normalizedMsg.senderId
                  : { _id: normalizedMsg.senderId._id || normalizedMsg.senderId })
                : null;

              return {
                ...normalizedMsg,
                cipherText: decryptedText,
                // Explicitly preserve senderId structure - always ensure it's { _id: "..." }
                senderId: preservedSenderId,
                // CRITICAL: Preserve loading and mediaUrl
                loading: normalizedMsg.loading || false,
                mediaUrl: normalizedMsg.mediaUrl || null,
              };
            } catch (err) {
              // Decryption failed - check if it's encrypted hex
              const isEncryptedHex = msg?.cipherText && /^[0-9a-fA-F\s]+$/.test(msg.cipherText.replace(/\s/g, ''));
              if (isEncryptedHex) {
                // Ensure senderId is always an object with _id property
                const preservedSenderId = msg.senderId
                  ? (typeof msg.senderId === 'object' && msg.senderId._id
                    ? msg.senderId
                    : { _id: msg.senderId._id || msg.senderId })
                  : null;

                return {
                  ...msg,
                  cipherText: "[Encrypted message - unable to decrypt]",
                  decryptionFailed: true,
                  // Explicitly preserve senderId structure
                  senderId: preservedSenderId,
                  // CRITICAL: Preserve loading and mediaUrl
                  loading: msg.loading || false,
                  mediaUrl: msg.mediaUrl || null,
                };
              }
              // Preserve senderId structure even if no decryption needed
              const preservedSenderId = msg.senderId
                ? (typeof msg.senderId === 'object' && msg.senderId._id
                  ? msg.senderId
                  : { _id: msg.senderId._id || msg.senderId })
                : null;

              return {
                ...msg,
                senderId: preservedSenderId,
                // CRITICAL: Preserve loading and mediaUrl
                loading: msg.loading || false,
                mediaUrl: msg.mediaUrl || null,
              };
            }
          })
        ).then((decryptedRest) => {
          // Update with fully decrypted messages
          // Update signature after background decryption (use messageId, not _id)
          // Use the ORIGINAL allMessages, not decrypted messages, to match the check format
          const fullMessageCount = allMessages.length;
          const fullFirstMessageId = allMessages[0]?.messageId || '';
          const fullLastMessageId = allMessages[allMessages.length - 1]?.messageId || '';
          const fullContentSignature = allMessages.slice(0, 20).map(m => ({
            messageId: m.messageId,
            cipherText: m.cipherText?.substring(0, 50) || '',
            iv: m.iv || '',
            senderId: m.senderId?._id || m.senderId || ''
          })).map(m => `${m.messageId}:${m.cipherText}:${m.iv}:${m.senderId}`).join('|');
          const fullSignature = `${fullMessageCount}:${fullFirstMessageId}:${fullLastMessageId}:${fullContentSignature}`;
          lastDecryptedRef.current = fullSignature;
          setMessages([...decryptedVisible, ...decryptedRest]);
        });
      }
    };
    if (!isMessageLoading) {
      decryptAllMessages();
    }
  }, [allMessages, getSecretKey, isMessageLoading]);

  const handleCopy = async (chat) => {
    try {
      // for copy image
      if (chat?.mediaUrl) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = chat.mediaUrl;

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const item = new ClipboardItem({ "image/png": blob });
          navigator.clipboard.write([item]);
          toast.success("Image copied to clipboard!");
        });
      } else if (chat?.cipherText) {
        // for copy text
        await navigator.clipboard.writeText(chat.cipherText);
        toast.success("Text copied to clipboard!");
      }
    } catch (err) {
      toast.error("Failed to copy!");
    } finally {
      setOpenDropdownIndex(null);
    }
  };

  const deleteMessage = async (forWhichDelete) => {
    let response;
    try {
      if (forWhichDelete === "for me") {
        const endPoint = `messages/singleChat/${deleteMessageId}`;
        response = await deleteApiCaller(endPoint);
      } else {
        const endPoint = `messages/${deleteMessageId}`;
        response = await deleteApiCaller(endPoint);
      }
      if (response.status === 200) {
        setAllMessages(
          allMessages.filter((msg) => msg._id !== deleteMessageId)
        );

        setChatUsers((pre) => {
          const index = pre.findIndex((user) => user._id === activeChat._id);
          const filteredMessages = allMessages.filter(
            (msg) => msg._id !== deleteMessageId
          );
          if (index !== -1) {
            pre[index].latestMessage =
              filteredMessages.length > 0 ? filteredMessages[0] : {};
          }
          return [...pre];
        });

        setDeleteModal(false);
        setDeleteMessageId(null);
        setOpenDropdownIndex(null);
        toast.success("Message deleted successfully!");
      } else {
        toast.error(response?.response?.data?.message || response.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error deleting message");
    }
  };

  const handleEdit = (message) => {
    setMessageToEdit(message);
    setEditedText(message.cipherText || "");
    setEditModalOpen(true);
    setOpenDropdownIndex(null);
  };
  const containsLink = (message) => {
    // Match URLs with or without protocol (http/https)
    const urlRegex =
      /(?:https?:\/\/)?(?:www\.)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/g;
    const urls = message.match(urlRegex);
    return urls ? urls[0] : null;
  };

  const handleUpdateMessage = async (message, newText) => {
    setIsEditLoading(true);
    if (message.cipherText === editedText) {
      setIsEditLoading(false);
      setEditModalOpen(false);
      return;
    }
    try {
      const getKey = await decryptText(
        chatConversationId?.AESKeyOfUser,
        userInfo?.encryptedPrivateKey
      );
      const { iv, encryptedData } = await encrypt(newText, getKey);
      if (!iv && !encryptedData) {
        setIsEditLoading(false);
        return;
      }
      const endPoint = `messages/edit`;
      const payload = {
        messageId: message._id,
        iv: iv,
        cipherText: encryptedData,
        isLink: containsLink(newText) ? true : false,
      };
      const response = await putApiCaller(endPoint, payload);
      if (response.status === 200) {
        setAllMessages(
          allMessages.map((msg) =>
            msg._id === message._id
              ? {
                ...msg,
                iv: iv,
                isEdited: true,
                cipherText: encryptedData,
                messageType: containsLink(newText) ? "LINK" : "TEXT",
              }
              : msg
          )
        );
        setEditingMessage(null);
      } else {
        toast.error(response?.response?.data?.message || response.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsEditLoading(false);
      setEditModalOpen(false);
    }
  };

  const handleEmojiClick = (emojiObject) => {
    setEditedText((prev) => prev + emojiObject.emoji);
  };

  if (isDecryptionLoading) {
    return <></>;
  }

  // Typing Indicator Component
  const TypingIndicator = () => (
    <div className="bg-white border border-zinc-200 rounded-full px-4 py-3 shadow-sm w-fit ml-12 mb-4 flex items-center gap-1.5">
      <div className="flex gap-1 items-center">
        <div
          className="w-2 h-2 rounded-full bg-zinc-400 typing-dot"
          style={{ animationDelay: '0ms' }}
        ></div>
        <div
          className="w-2 h-2 rounded-full bg-zinc-400 typing-dot"
          style={{ animationDelay: '150ms' }}
        ></div>
        <div
          className="w-2 h-2 rounded-full bg-zinc-400 typing-dot"
          style={{ animationDelay: '300ms' }}
        ></div>
      </div>
    </div>
  );

  return (
    <>
      {/* Typing Indicator - appears at bottom where next message will be */}
      {isTyping && <TypingIndicator />}
      {!isMessageLoading &&
        messages?.map((chat, index) => {
          // Ensure senderId is properly structured for comparison
          // Handle both object format { _id: "..." } and string format
          const senderIdValue = chat?.senderId?._id || (typeof chat?.senderId === 'string' ? chat.senderId : null);
          const isSender = String(senderIdValue) === String(uid);

          const prevMsg = index > 0 ? messages[index - 1] : null;
          const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;
          const stackPosition = getMessageStackPosition(chat, prevMsg, nextMsg, uid);
          const borderRadiusClasses = getBorderRadiusClasses(stackPosition, isSender);

          const isLast = stackPosition === 'bottom';
          const isSingle = stackPosition === 'single';
          const isFirst = stackPosition === 'top';
          const isMiddle = stackPosition === 'middle';
          const shouldShowAvatar = isLast || isSingle;

          // Check if we need a date separator
          const showDateSeparator = index === 0 || isDifferentDate(prevMsg, chat);

          return (
            <React.Fragment key={chat._id}>
              {/* Date Separator */}
              {showDateSeparator && (
                <div className="flex justify-center my-4 -mx-4">
                  <div className="backdrop-blur-md bg-zinc-50/80 dark:bg-slate-800/80 px-4 py-2 rounded-full shadow-sm text-sm text-zinc-600 dark:text-zinc-300 font-medium">
                    {formatDateSeparator(chat.sentTime)}
                  </div>
                </div>
              )}

              <div
                id={chat.messageId || chat._id} // Add ID for deep linking
                className={`group chat relative flex items-end gap-2 message-enter ${isSender ? "chat-end justify-end" : "chat-start justify-start"
                  } ${activeMessageId === (chat.messageId || chat._id) ? "transition-all duration-300 ring-2 ring-indigo-500 ring-offset-2 rounded-lg bg-indigo-50/10" : ""}`}
              >
                {/* Avatar or Spacer - Only for received messages */}
                {!isSender && (
                  <div className="w-8 h-8 flex-shrink-0 mb-1">
                    {shouldShowAvatar ? (
                      <img
                        src={activeChat?.user?.mediaUrl || activeChat?.user?.avtarImage || activeChat?.user?.userImage || "/user.png"}
                        alt={activeChat?.user?.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8"></div>
                    )}
                  </div>
                )}
                {/* Spacer for sent messages to maintain alignment */}
                {isSender && !shouldShowAvatar && (
                  <div className="w-8 h-8 flex-shrink-0"></div>
                )}

                {/* Message Bubble Container */}
                <div className="relative">
                  {/* Reaction Trigger Button */}
                  <button
                    className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-indigo-500 cursor-pointer z-10"
                    style={{ [isSender ? "left" : "right"]: "-32px" }}
                    onClick={() => setReactionPickerOpen(chat._id)}
                    aria-label="Add reaction"
                  >
                    <HiOutlineEmojiHappy size={20} />
                  </button>

                  <div
                    className={`text-sm relative max-w-md break-all ${chat?.mediaUrl && chat?.mediaId?.fileType === "image"
                      ? chat.cipherText
                        ? "p-1"
                        : "p-0"
                      : "px-3 py-3 pr-6"
                      } ${!isSender
                        ? "bg-white dark:bg-slate-800 text-zinc-900 dark:text-zinc-100 border border-zinc-200 dark:border-slate-700 shadow-sm"
                        : "bg-gradient-to-r from-blue-600 to-indigo-600 !text-white shadow-md"
                      } ${borderRadiusClasses}`}
                    style={{
                      ...(isSender ? {
                        background: 'linear-gradient(to right, rgb(37 99 235), rgb(79 70 229))',
                        color: 'white'
                      } : {})
                    }}
                  >
                    <div
                      className={`absolute z-10 ${chat?.mediaUrl && chat?.mediaId?.fileType === "image"
                        ? "top-2 right-2" // or custom positions for image
                        : "top-1 right-1 mt-1"
                        }`}
                      ref={(el) => (dropdownRefs.current[chat._id] = el)}
                    >
                      {(chat?.mediaId?.fileType === "image" ||
                        chat?.cipherText ||
                        chat.senderId?._id === uid) &&
                        chat?.messageType !== "LOCATION" &&
                        !chat.loading && (
                          <div className="group absolute top-0 right-0 p-1 pointer-events-auto z-20">
                            <div
                              className={`${chat.mediaUrl &&
                                chat?.mediaId?.fileType === "image" &&
                                "bg-black/20 rounded-full p-1 transition-all group-hover:bg-black cursor-pointer"
                                }`}
                              onClick={() =>
                                setOpenDropdownIndex(
                                  openDropdownIndex === chat._id ? null : chat._id
                                )
                              }
                            >
                              <IoChevronDownOutline
                                size={12}
                                className="text-white cursor-pointer"
                              />
                            </div>
                          </div>
                        )}
                      {openDropdownIndex === chat._id &&
                        !chat.loading &&
                        chat?.messageType !== "LOCATION" &&
                        (chat?.mediaId?.fileType === "image" ||
                          chat?.mediaId?.fileType === "document" ||
                          chat?.mediaId?.fileType === "audio" || // Added audio here
                          chat?.cipherText ||
                          chat.senderId?._id === uid) && (
                          <div
                            className={`absolute right-[-10px] w-35 bg-white shadow-lg rounded-md z-30 border ${chat?.mediaId?.fileType === "document" ||
                              chat?.mediaId?.fileType === "audio" ||
                              chat?.mediaId?.fileType === "video"
                              ? "top-0"
                              : chat?.senderId?._id === uid && index === 0
                                ? "top-[-130px]"
                                : "top-[10px]"
                              }`}
                          >
                            <ul className="py-1 text-sm text-gray-700">
                              {(chat?.mediaId?.fileType === "image" ||
                                chat?.cipherText ||
                                activeChat?.user?.isBlocked) &&
                                chat.mediaId.fileType !== "document" &&
                                chat.mediaId.fileType !== "audio" &&
                                chat.mediaId.fileType !== "video" && (
                                  <li
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => handleCopy(chat)}
                                  >
                                    Copy
                                  </li>
                                )}
                              {chat?.senderId?._id === uid &&
                                !activeChat?.user?.isBlocked && (
                                  <>
                                    {chat?.cipherText &&
                                      chat?.messageType !== "CONTACT" && (
                                        <li
                                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                          onClick={() => handleEdit(chat)}
                                        >
                                          Edit
                                        </li>
                                      )}
                                    <li
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                      onClick={() => {
                                        setDeleteModal(true);
                                        setDeleteMessageId(chat._id);
                                        setIsDeleteFor("for me");
                                      }}
                                    >
                                      Delete for me
                                    </li>
                                    <li
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                      onClick={() => {
                                        setDeleteModal(true);
                                        setDeleteMessageId(chat._id);
                                        setIsDeleteFor("for all");
                                      }}
                                    >
                                      Delete for all
                                    </li>
                                  </>
                                )}
                            </ul>
                          </div>
                        )}
                    </div>

                    {chat?.mediaId?.fileType === "document" && (
                      <div
                        onClick={() => !chat.loading && downloadFile(chat.mediaUrl)}
                        className={`${chat.loading ? "cursor-progress" : "cursor-pointer"
                          } relative flex flex-col items-center gap-2 pt-5 ${chat.cipherText
                            ? "border-b border-zinc-200 pb-1 mb-1"
                            : ""
                          }`}
                      >
                        <div className={`flex items-center justify-center w-[110px] h-[110px] min-w-[110px] min-h-[110px] py-5 rounded-lg ${!isSender
                          ? "bg-zinc-50 border border-zinc-200"
                          : "bg-blue-500/30 border border-blue-400/30"
                          }`}>
                          <FileIcon url={chat.mediaId.fileUrl} />
                        </div>
                        <div className={`whitespace-pre-line max-w-50 truncate ${!isSender ? "text-zinc-800 dark:text-zinc-100" : "!text-white"
                          }`}>
                          {
                            chat?.mediaId?.fileUrl?.split?.("/")?.[
                            chat?.mediaId?.fileUrl?.split("/")?.length - 1
                            ]
                          }
                        </div>
                      </div>
                    )}
                    {chat?.mediaId?.fileType === "video" && (
                      <div
                        onClick={() => !chat.loading && downloadFile(chat.mediaUrl)}
                        className={`${chat.loading ? "cursor-progress" : "cursor-pointer"
                          } ${chat.cipherText
                            ? "border-b border-zinc-200 pb-2 mb-2"
                            : ""
                          } relative flex flex-col items-center gap-2`}
                      >
                        <div className={`flex items-center justify-center w-full h-full py-5 rounded-lg ${!isSender
                          ? "bg-zinc-50 border border-zinc-200"
                          : "bg-blue-500/30 border border-blue-400/30"
                          }`}>
                          <FaFileVideo className={`size-20 ${!isSender ? "text-purple-500" : "!text-white"
                            }`} />
                        </div>
                        {/* <div className="whitespace-pre-line max-w-50 truncate">
                          {
                            chat?.mediaId?.fileUrl?.split?.("/")?.[
                              chat?.mediaId?.fileUrl?.split("/")?.length - 1
                            ]
                          }
                        </div> */}
                      </div>
                    )}
                    {chat.mediaUrl && chat?.mediaId?.fileType === "audio" && (
                      <div className="relative">
                        <VoiceMessageBubble
                          url={chat.mediaUrl}
                          disabled={chat.loading}
                        />
                      </div>
                    )}

                    {chat.mediaUrl && chat?.mediaId?.fileType === "image" && (
                      <div
                        className={`relative w-[200px] h-[200px] p-0 rounded-2xl bg-[#f0f0f0] dark:bg-gray-800 group ring-1 ring-white/20 ${chat.cipherText ? "mb-2" : ""
                          }`}
                      >
                        <div
                          className={`relative w-full h-full rounded-2xl overflow-hidden ${!chat?.cipherText && !isSender
                            ? "rounded-bl-none"
                            : "rounded-br-none"
                            }`}
                        >
                          <img
                            onClick={() =>
                              !chat.loading && downloadFile(chat.mediaUrl)
                            }
                            src={chat.mediaUrl}
                            alt="media"
                            className={`w-full h-full object-cover rounded-2xl ${chat.loading ? "cursor-progress" : "cursor-pointer"
                              } ${!chat?.cipherText ? "rounded-br-none" : ""}`}
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-2xl pointer-events-none" />

                          {chat.loading && (
                            <div className="absolute inset-0 bg-black/40 z-10 flex justify-center items-center rounded-2xl">
                              <FadeLoader
                                color="#7104F8"
                                loading={true}
                                size={12}
                              />
                            </div>
                          )}
                          {/* Timestamp */}
                          {chat?.mediaId && !chat?.cipherText && (
                            <div className="absolute bottom-1 right-2 text-[10px] px-1.5 py-0.5 rounded">
                              <div
                                className={`text-[10px] inline-flex items-center gap-1 ${isSender
                                  ? "!text-blue-100"
                                  : "text-zinc-500"
                                  }`}
                              >
                                <span>{formatDate(chat.sentTime)}</span>
                                {chat?.senderId?._id === uid && (
                                  <>
                                    {chat.isSeen ? (
                                      <BsCheckAll className="!text-blue-100" />
                                    ) : (
                                      <BsCheckLg className="!text-blue-100" />
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {chat?.messageType === "LOCATION" && (
                      <MapMessage text={chat?.cipherText} />
                    )}

                    {chat?.messageType === "CONTACT" && (
                      <div className="flex items-center gap-3 p-2 rounded-lg">
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                          {chat?.sharedContact?.avtarImage ||
                            chat?.sharedContact?.userImage ? (
                            <img
                              src={
                                chat?.sharedContact?.avtarImage ||
                                chat?.sharedContact?.userImage
                              }
                              alt="contact"
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <FaUser className="text-zinc-500 text-xl" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className={`font-medium ${isSender ? "!text-white" : "text-zinc-900 dark:text-zinc-100"
                            }`}>
                            {chat?.sharedContact?.name}
                          </div>
                          <div className={`text-sm ${isSender ? "!text-blue-100" : "text-zinc-500 dark:text-zinc-400"
                            }`}>
                            {chat?.sharedContact?.phone}
                          </div>
                        </div>
                      </div>
                    )}

                    {chat?.messageType !== "LOCATION" && chat.cipherText && (
                      <div className="flex items-end gap-1 flex-wrap">
                        <div
                          className={`whitespace-pre-wrap break-words break-normal text-left flex-1 ${chat?.mediaUrl && chat?.mediaId?.fileType === "image"
                            ? "px-3 py-1"
                            : ""
                            } ${!isSender
                              ? "text-zinc-800 dark:text-zinc-100"
                              : "!text-white"
                            }`}
                          style={{
                            wordBreak: "normal",
                            overflowWrap: "break-word",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: linkify(chat.cipherText),
                          }}
                        ></div>
                        {chat?.mediaId?.fileType !== "image" && (
                          <div className={`text-[10px] inline-flex items-center gap-1 flex-shrink-0 ${isSender ? "!text-blue-100" : "text-zinc-500"
                            }`}>
                            <span>{formatDate(chat.sentTime)}</span>
                            {chat?.senderId?._id === uid && (
                              <>
                                {chat.isSeen ? (
                                  <BsCheckAll className="!text-blue-100" />
                                ) : (
                                  <BsCheckLg className="!text-blue-100" />
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {chat?.mediaId?.fileType !== "image" && !chat.cipherText && (
                      <div className="flex items-end gap-1 justify-end">
                        <div className={`text-[10px] inline-flex items-center gap-1 ${isSender ? "!text-blue-100" : "text-zinc-500"
                          }`}>
                          <span>{formatDate(chat.sentTime)}</span>
                          {chat?.senderId?._id === uid && (
                            <>
                              {chat.isSeen ? (
                                <BsCheckAll className="!text-blue-100" />
                              ) : (
                                <BsCheckLg className="!text-blue-100" />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {chat?.isEdited && (
                      <div className="flex items-end gap-2 pt-1 flex-row-reverse">
                        <div className={`text-[10px] inline-flex items-center gap-1 ${isSender ? "!text-blue-100" : "text-zinc-500"
                          }`}>
                          <span>{formatDate(chat.sentTime)}</span>
                          {chat?.senderId?._id === uid && (
                            <>
                              {chat.isSeen ? (
                                <BsCheckAll className="!text-blue-100" />
                              ) : (
                                <BsCheckLg className="!text-blue-100" />
                              )}
                            </>
                          )}
                        </div>
                        <p
                          className={`${isSender
                            ? "!text-blue-100 text-right font-normal"
                            : "text-zinc-500 text-right font-normal"
                            } text-[10px]`}
                        >
                          Edited
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Reaction Pills */}
                  {chat.reactions && chat.reactions.length > 0 && (
                    <ReactionPills
                      reactions={chat.reactions}
                      messageId={chat._id}
                      currentUserId={uid}
                      socket={socket}
                    />
                  )}

                  {/* Reaction Picker Popover */}
                  {reactionPickerOpen === chat._id && (
                    <ReactionPicker
                      messageId={chat._id}
                      onClose={() => setReactionPickerOpen(null)}
                      position={isSender ? "left" : "right"}
                      socket={socket}
                    />
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      <Modal
        isOpen={deleteModal}
        onClose={() => {
          setDeleteMessageId(null);
          setDeleteModal(false);
        }}
        onConfirm={() => deleteMessage(isDeleteFor)}
        description={
          isDeleteFor === "for me"
            ? "Are you sure you want to delete this message for me?"
            : "Are you sure you want to delete this message for all?"
        }
        title="Delete Message"
      />
      <ResponsiveModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        center
        closeIcon={false}
        classNames={{
          modal: "custom-modal",
          overlay: "custom-overlay",
        }}
        styles={{
          modal: {
            padding: 0,
            width: "100%",
            maxHeight: "500px",
            maxWidth: "35rem", // ~512px
            borderRadius: "0.75rem", // rounded-xl
          },
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center border-b p-4">
          <h2 className="text-lg font-semibold text-gray-800">Edit Message</h2>
        </div>

        {/* Message Bubble Preview */}
        <div className="flex">
          <div className="p-4 overflow-y-auto flex-1 flex justify-center items-center ">
            <div className=" bg-[#7104F8] text-white px-4 py-3 rounded-lg max-w-sm w-full break-words whitespace-pre-wrap text-sm shadow">
              {editedText || "Your message preview will appear here..."}
            </div>
          </div>
        </div>

        {/* Footer */}
        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            className="absolute top-0 right-15 bg-white shadow-lg rounded-md"
          >
            <EmojiPicker theme={theme} onEmojiClick={handleEmojiClick} />
          </div>
        )}
        <div className="border-t p-3 flex items-end gap-2">
          <textarea
            rows={4}
            className="flex-1 resize-none px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7104F8] text-sm transition-all duration-150 max-h-[200px]"
            placeholder="Edit message..."
            value={editedText}
            onChange={(e) => {
              setEditedText(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              const maxHeight = 200;
              const scrollHeight = el.scrollHeight;
              el.style.overflowY = scrollHeight > maxHeight ? "auto" : "";
              el.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
            }}
          />
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-gray-900"
              onClick={() => setShowEmojiPicker((prev) => !prev)}
            >
              <FaRegSmile className="w-6 h-6" />
            </button>
            <button
              onClick={() => handleUpdateMessage(messageToEdit, editedText)}
              disabled={isEditLoading || !editedText.trim()}
              className="bg-[#7104F8] hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50"
            >
              {isEditLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
